export type StorefrontTone = "antique" | "modern" | "hybrid" | "custom";
export type HeroStyle = "banner" | "split" | "minimal";
export type ProductGridStyle = "dense" | "spacious" | "gallery";
export type MarketplaceName = "ebay" | "facebook" | "etsy" | "amazon";

export interface StorefrontConfig {
  id: string;
  slug: string;
  displayName: string;
  legalName?: string;
  branding: {
    logoUrl?: string;
    primaryColor: string;
    secondaryColor?: string;
    fontFamily?: string;
    tone: StorefrontTone;
  };
  layout: {
    heroStyle: HeroStyle;
    productGridStyle: ProductGridStyle;
    showSearch: boolean;
    showCategories: boolean;
    showAboutSection: boolean;
  };
  marketplaceLinks: {
    ebay?: string;
    facebookMarketplace?: string;
    etsy?: string;
    amazon?: string;
  };
  policies: {
    disclaimer: string;
    redirectNotice: string;
  };
  features: {
    enableWishlist: boolean;
    enableCategories: boolean;
    enableFeatured: boolean;
  };
}

export interface StorefrontProduct {
  id: string;
  title: string;
  price: string;
  currency: string;
  imageUrl: string;
  externalUrl: string;
  marketplace: MarketplaceName;
}

export interface StorefrontProductsResponse {
  products: StorefrontProduct[];
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isString = (value: unknown): value is string => typeof value === "string";

const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";

const isNonEmptyString = (value: unknown): value is string =>
  isString(value) && value.trim().length > 0;

const isOneOf = <T extends string>(value: unknown, options: readonly T[]): value is T =>
  isString(value) && options.includes(value as T);

const heroStyles: HeroStyle[] = ["banner", "split", "minimal"];
const gridStyles: ProductGridStyle[] = ["dense", "spacious", "gallery"];
const tones: StorefrontTone[] = ["antique", "modern", "hybrid", "custom"];
const marketplaces: MarketplaceName[] = ["ebay", "facebook", "etsy", "amazon"];

const isStorefrontProduct = (value: unknown): value is StorefrontProduct => {
  if (!isObject(value)) {
    return false;
  }

  return (
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.title) &&
    isNonEmptyString(value.price) &&
    isNonEmptyString(value.currency) &&
    isNonEmptyString(value.imageUrl) &&
    isNonEmptyString(value.externalUrl) &&
    isOneOf(value.marketplace, marketplaces)
  );
};

const isStorefrontProductsResponse = (
  value: unknown
): value is StorefrontProductsResponse => {
  if (!isObject(value)) {
    return false;
  }

  if (!Array.isArray(value.products)) {
    return false;
  }

  return value.products.every(isStorefrontProduct);
};

export const isStorefrontConfig = (value: unknown): value is StorefrontConfig => {
  if (!isObject(value)) {
    return false;
  }

  if (!isNonEmptyString(value.id) || !isNonEmptyString(value.slug)) {
    return false;
  }

  if (!isNonEmptyString(value.displayName)) {
    return false;
  }

  if (!isObject(value.branding)) {
    return false;
  }

  if (
    !isNonEmptyString(value.branding.primaryColor) ||
    !isOneOf(value.branding.tone, tones)
  ) {
    return false;
  }

  if (!isObject(value.layout)) {
    return false;
  }

  if (
    !isOneOf(value.layout.heroStyle, heroStyles) ||
    !isOneOf(value.layout.productGridStyle, gridStyles) ||
    !isBoolean(value.layout.showSearch) ||
    !isBoolean(value.layout.showCategories) ||
    !isBoolean(value.layout.showAboutSection)
  ) {
    return false;
  }

  if (!isObject(value.marketplaceLinks)) {
    return false;
  }

  if (!isObject(value.policies)) {
    return false;
  }

  if (
    !isNonEmptyString(value.policies.disclaimer) ||
    !isNonEmptyString(value.policies.redirectNotice)
  ) {
    return false;
  }

  if (!isObject(value.features)) {
    return false;
  }

  return (
    isBoolean(value.features.enableWishlist) &&
    isBoolean(value.features.enableCategories) &&
    isBoolean(value.features.enableFeatured)
  );
};

export const parseStorefrontConfig = (value: unknown): StorefrontConfig => {
  if (!isStorefrontConfig(value)) {
    throw new Error("Invalid storefront config payload.");
  }

  return value;
};

export const parseStorefrontProductsResponse = (
  value: unknown
): StorefrontProductsResponse => {
  if (!isStorefrontProductsResponse(value)) {
    throw new Error("Invalid storefront products payload.");
  }

  return value;
};
